import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import FiltersLabel from '../../components/FiltersLabel';
import {
    fetchErrorLogs,
    fetchErrorLogById,
    markAllErrorLogsAsRead,
    clearSelectedLog
} from '../../slices/error-logs.slice';
import { fetchUsers } from '../../slices/user.slice';
import Loading from '../../components/Loading';
import { DatePicker } from '../../components/DateTime';
import {
    Badge,
    Button,
    ButtonGroup,
    Label,
    ListGroup,
    ListGroupItem,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import { getFiterDatesLabel } from '../../utils/dates';
import SearchBar from '../../components/SearchBar';
import Avatar from '../../components/Avatar';
import ErrorLogDetailPanel from './ErrorLogDetailPanel';
import './ErrorLogs.scss';

const ERROR_TYPE_CONFIG = {
    server: { color: 'danger', icon: 'fa-server', label: 'Server' },
    toast: { color: 'warning', icon: 'fa-comment-exclamation', label: 'Toast' },
    exception: { color: 'purple', icon: 'fa-bug', label: 'Exception' }
};

const ErrorLogs = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { logId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { list: errorLogs, status, pagination } = useSelector(state => state.errorLogs);
    const { list: users } = useSelector(state => state.users);
    const [searchValue, setSearchInput] = useState(searchParams.get('search') || '');
    const [searchModalValue, setSearchModalValue] = useState('');
    const debounceRef = useRef(null);
    const [filterModal, setFilterModal] = useState({ isOpen: false, filter: '', filterName: '' });

    const isPanelOpen = !!logId;

    const selectErrorLog = (errorLogId) => {
        navigate(`/error-logs/${errorLogId}${window.location.search}`);
    };

    const closePanel = () => {
        dispatch(clearSelectedLog());
        navigate(`/error-logs${window.location.search}`);
    };

    useEffect(() => {
        if (logId) {
            dispatch(fetchErrorLogById(logId));
        }
    }, [dispatch, logId]);

    const parseURLFilters = () => {
        const errorTypesParam = searchParams.get('errorTypes');
        const usersParam = searchParams.get('users');
        const dateFromParam = searchParams.get('dateFrom');
        const dateToParam = searchParams.get('dateTo');
        const searchParam = searchParams.get('search');
        const pageParam = searchParams.get('page');
        const unreadOnlyParam = searchParams.get('unreadOnly');

        return {
            errorTypes: errorTypesParam ? errorTypesParam.split(',') : [],
            users: usersParam ? usersParam.split(',') : [],
            unreadOnly: unreadOnlyParam === 'true',
            createdAt: {
                from: dateFromParam ? new Date(dateFromParam) : null,
                to: dateToParam ? new Date(dateToParam) : null
            },
            search: searchParam || '',
            page: pageParam ? parseInt(pageParam, 10) : 1
        };
    };

    const [filterBy, setFilterBy] = useState(parseURLFilters());

    const toggleFilterModal = (filter, filterName) => {
        setFilterModal({ isOpen: !filterModal.isOpen, filter, filterName });
    };

    const formatDateForURL = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const updateURLParams = (newFilters) => {
        const params = new URLSearchParams();

        if (newFilters.errorTypes.length > 0) {
            params.set('errorTypes', newFilters.errorTypes.join(','));
        }
        if (newFilters.users.length > 0) {
            params.set('users', newFilters.users.join(','));
        }
        if (newFilters.unreadOnly) {
            params.set('unreadOnly', 'true');
        }
        if (newFilters.createdAt.from) {
            params.set('dateFrom', formatDateForURL(newFilters.createdAt.from));
        }
        if (newFilters.createdAt.to) {
            params.set('dateTo', formatDateForURL(newFilters.createdAt.to));
        }
        if (newFilters.search) {
            params.set('search', newFilters.search);
        }
        if (newFilters.page && newFilters.page !== 1) {
            params.set('page', newFilters.page);
        }

        setSearchParams(params);
    };

    const handleSearch = (event) => {
        event.preventDefault();
        const query = event.target.value;
        setSearchInput(query);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            setFilterBy(prev => {
                const newFilters = { ...prev, search: query, page: 1 };
                updateURLParams(newFilters);
                return newFilters;
            });
        }, 400);
    };

    const toggleFilter = (filterKey, value) => {
        setFilterBy((prevFilterBy) => {
            const isSelected = prevFilterBy[filterKey].includes(value);
            const newFilters = {
                ...prevFilterBy,
                [filterKey]: isSelected
                    ? prevFilterBy[filterKey].filter((item) => item !== value)
                    : [...prevFilterBy[filterKey], value],
                page: 1
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const filterTextUser = (user) => {
        return [user.firstName, user.lastName, user.username, user.email]
            .some(field => field?.toLowerCase().includes(searchModalValue.toLowerCase()));
    };

    const handleCreatedAt = (period) => (date) => {
        setFilterBy((prevFilterBy) => {
            const newFilters = {
                ...prevFilterBy,
                createdAt: {
                    ...prevFilterBy.createdAt,
                    [period]: date
                },
                page: 1
            };
            updateURLParams(newFilters);
            return newFilters;
        });
    };

    const areFiltersActive = useMemo(() => {
        const { errorTypes, users, createdAt, unreadOnly } = filterBy;

        return (
            errorTypes.length > 0 ||
            users.length > 0 ||
            unreadOnly ||
            createdAt.from !== null ||
            createdAt.to !== null
        );
    }, [filterBy]);

    const clearFilters = () => {
        const newFilters = {
            errorTypes: [],
            users: [],
            unreadOnly: false,
            createdAt: { from: null, to: null },
            search: '',
            page: 1
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handlePageChange = (newPage) => {
        const newFilters = {
            ...filterBy,
            page: newPage
        };
        setFilterBy(newFilters);
        updateURLParams(newFilters);
    };

    const handleMarkAllAsRead = () => {
        dispatch(markAllErrorLogsAsRead());
    };

    useEffect(() => {
        const params = {
            errorTypes: filterBy.errorTypes,
            users: filterBy.users,
            page: filterBy.page,
            unreadOnly: filterBy.unreadOnly
        };

        if (filterBy.search) {
            params.search = filterBy.search;
        }

        if (filterBy.createdAt.from) {
            const fromDate = new Date(filterBy.createdAt.from);
            fromDate.setHours(0, 0, 0, 0);
            params.dateFrom = fromDate.toISOString();
        }

        if (filterBy.createdAt.to) {
            const toDate = new Date(filterBy.createdAt.to);
            toDate.setHours(23, 59, 59, 999);
            params.dateTo = toDate.toISOString();
        }

        dispatch(fetchErrorLogs(params));
    }, [dispatch, filterBy]);

    useEffect(() => {
        dispatch(fetchUsers());
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="error-log-view page px-4 py-3 d-flex flex-column overflow-y-auto">
            <PageHeader
                title="Error Logs"
                searchBar={{ searchValue, handleSearch }}
            />

            <div className="d-flex justify-content-start align-items-center gap-3 flex-wrap">
                <FiltersLabel active={areFiltersActive} onClear={clearFilters}/>

                <ButtonGroup className="gap-2">
                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('errorTypes', 'Error Types')}>
                        {filterBy.errorTypes.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.errorTypes.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-exclamation-triangle me-2" />
                        )}
                        Error Types
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('users')}>
                        {filterBy.users.length > 0 ? (
                            <Badge color="success" className="me-2 text-white">
                                {filterBy.users.length}
                            </Badge>
                        ) : (
                            <span className="fas fa-users me-2" />
                        )}
                        Users
                    </Button>

                    <Button color="primary" outline size="sm" className="rounded-3"
                            onClick={() => toggleFilterModal('createdAt', 'dates')}>
                        {filterBy.createdAt.from || filterBy.createdAt.to ? (
                            <Badge color="success" className="me-2 text-white">
                                <span className="fas fa-calendar-check" />
                            </Badge>
                        ) : (
                            <span className="fas fa-calendar-week me-2" />
                        )}
                        {getFiterDatesLabel(filterBy.createdAt, 'Dates')}
                    </Button>

                    <Button
                        color={filterBy.unreadOnly ? 'success' : 'primary'}
                        outline={!filterBy.unreadOnly}
                        size="sm"
                        className="rounded-3"
                        onClick={() => {
                            const newFilters = {
                                ...filterBy,
                                unreadOnly: !filterBy.unreadOnly,
                                page: 1
                            };
                            setFilterBy(newFilters);
                            updateURLParams(newFilters);
                        }}
                    >
                        <span className="fas fa-envelope me-2" />
                        Unread Only
                    </Button>
                </ButtonGroup>

                <div className="flex-grow-1">
                    <span className="fas fa-list me-2 text-primary" />
                    showing {pagination.totalCount} result{pagination.totalCount === 1 ? '' : 's'}
                </div>

                <div className="text-primary">
                    <Button color="link" onClick={handleMarkAllAsRead} className="text-decoration-none">
                        <span className="fas fa-check-double me-1" /> Mark All Read
                    </Button>
                </div>
            </div>

            {status === 'loading' && <Loading length={3} />}

            {status === 'success' && (
                <div className="error-log-list">
                    {errorLogs.map((errorLog) => {
                        const typeConfig = ERROR_TYPE_CONFIG[errorLog.errorType] || ERROR_TYPE_CONFIG.exception;

                        return (
                            <div
                                key={errorLog._id}
                                className={`error-log-item my-2 p-3 bg-broken border-start d-flex justify-content-between error-type-${errorLog.errorType} ${logId === errorLog._id ? 'selected' : ''} ${!errorLog.isRead ? 'unread' : ''}`}
                                onClick={() => selectErrorLog(errorLog._id)}
                            >
                                <div className="error-type-icon">
                                    <span className={`fas ${typeConfig.icon}`} />
                                </div>

                                {!errorLog.isRead && <div className="unread-indicator" />}

                                <div className="flex-grow-1 overflow-hidden">
                                    <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                        <Badge
                                            color={typeConfig.color}
                                            className={typeConfig.color === 'purple' ? 'bg-purple' : ''}
                                            style={{ borderRadius: 0 }}
                                        >
                                            <span className={`fas ${typeConfig.icon} me-1`} />
                                            {typeConfig.label}
                                        </Badge>
                                        {errorLog.occurrenceCount > 1 && (
                                            <Badge color="secondary" style={{ borderRadius: 0 }}>
                                                <span className="fas fa-layer-group me-1" />
                                                {errorLog.occurrenceCount}x
                                            </Badge>
                                        )}
                                        {errorLog.responseStatus && (
                                            <Badge
                                                color={errorLog.responseStatus >= 500 ? 'danger' : 'warning'}
                                                style={{ borderRadius: 0 }}
                                            >
                                                {errorLog.responseStatus}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="mb-1 text-truncate">{errorLog.message}</p>
                                    {errorLog.requestUrl && (
                                        <p className="mb-0 small text-white-50 text-truncate">
                                            <span className="fas fa-link me-1" />
                                            {errorLog.requestMethod} {errorLog.requestUrl}
                                        </p>
                                    )}
                                    {errorLog.frontendUrl && !errorLog.requestUrl && (
                                        <p className="mb-0 small text-white-50 text-truncate">
                                            <span className="fas fa-map-marker-alt me-1" />
                                            {errorLog.frontendUrl}
                                        </p>
                                    )}
                                </div>

                                <div className="d-flex flex-column align-items-end gap-2 ms-3 flex-shrink-0">
                                    <div className="small text-white-50">
                                        <span className="fas fa-calendar me-2" />
                                        {new Date(errorLog.lastOccurredAt || errorLog.createdAt).toLocaleString()}
                                    </div>
                                    {errorLog.user && (
                                        <div className="d-flex align-items-center gap-2 p-2 bg-black/30 rounded">
                                            <Avatar
                                                imageUrl={errorLog.user.profilePic?.imageUrl}
                                                name={`${errorLog.user.firstName} ${errorLog.user.lastName}`}
                                                size={28}
                                            />
                                            <span className="small">{errorLog.user.firstName} {errorLog.user.lastName}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {errorLogs.length === 0 && (
                        <div className="d-flex flex-column align-items-center py-5">
                            <span className="fas fa-circle-check fa-3x text-success mb-3 mt-5 pt-5" />
                            {areFiltersActive ? (
                                <>
                                    <p className="text-white-50 mb-3">No error logs match your filters</p>
                                    <Button color="primary" outline onClick={clearFilters}>
                                        Clear filters
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <h5 className="text-white mb-2">All Clear!</h5>
                                    <p className="text-white-50">No error logs recorded</p>
                                </>
                            )}
                        </div>
                    )}

                    {pagination.totalPages > 1 && (
                        <div className="d-flex justify-content-center align-items-center gap-3 my-4">
                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={pagination.currentPage === 1}
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                            >
                                <span className="fas fa-chevron-left me-2" />
                                Previous
                            </Button>

                            <span className="text-white">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </span>

                            <Button
                                color="primary"
                                outline
                                size="sm"
                                disabled={!pagination.hasMore}
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                            >
                                Next
                                <span className="fas fa-chevron-right ms-2" />
                            </Button>
                        </div>
                    )}

                    {/* Error Type Legend */}
                    <div className="error-type-legend">
                        <div className="legend-item server">
                            <div className="legend-dot"><span className="fas fa-server" /></div>
                            Server Error
                        </div>
                        <div className="legend-item toast-error">
                            <div className="legend-dot"><span className="fas fa-comment-exclamation" /></div>
                            Toast Error
                        </div>
                        <div className="legend-item exception">
                            <div className="legend-dot"><span className="fas fa-bug" /></div>
                            Exception
                        </div>
                    </div>
                </div>
            )}

            <Modal isOpen={filterModal.isOpen} toggle={() => toggleFilterModal()} size="lg" scrollable>
                <ModalHeader toggle={() => toggleFilterModal()}>
                    Filter by {filterModal.filterName || filterModal.filter}
                </ModalHeader>
                <ModalBody>
                    {filterModal.filter === 'errorTypes' && (
                        <div className="error-types-filter-modal">
                            <ListGroup>
                                {Object.entries(ERROR_TYPE_CONFIG).map(([type, config]) => (
                                    <ListGroupItem
                                        key={type}
                                        onClick={() => toggleFilter('errorTypes', type)}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.errorTypes.includes(type)}
                                        className={`border-0 border-start border-${config.color} border-4 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2`}
                                    >
                                        <div
                                            className={`rounded-circle bg-dark d-flex align-items-center justify-content-center text-${config.color} ${config.color === 'purple' ? 'bg-purple-border' : ''}`}
                                            style={{ width: '32px', height: '32px', border: '2px solid currentColor' }}
                                        >
                                            <span className={`fas ${config.icon} fa-sm`} />
                                        </div>
                                        <div className="flex-grow-1">
                                            <span className="fw-medium">{config.label}</span>
                                            <p className="mb-0 small text-white-50">
                                                {type === 'server' && 'API/HTTP errors from backend'}
                                                {type === 'toast' && 'Frontend toast error messages'}
                                                {type === 'exception' && 'Uncaught JavaScript exceptions'}
                                            </p>
                                        </div>
                                        {filterBy.errorTypes.includes(type) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}

                    {filterModal.filter === 'createdAt' && (
                        <div className="createdAt-filter-modal">
                            <Label>Date from</Label>
                            <DatePicker
                                value={filterBy.createdAt.from}
                                onChange={handleCreatedAt('from')}
                                placeholder="From"
                                clearable
                            />
                            <Label className="mt-4">Date to</Label>
                            <DatePicker
                                value={filterBy.createdAt.to}
                                onChange={handleCreatedAt('to')}
                                placeholder="To"
                                clearable
                            />
                        </div>
                    )}

                    {filterModal.filter === 'users' && (
                        <div className="users-filter-modal">
                            <SearchBar onChange={({ target }) => setSearchModalValue(target.value)} initialValue={searchModalValue} />
                            <ListGroup>
                                {users.map(user => filterTextUser(user) && (
                                    <ListGroupItem
                                        onClick={() => toggleFilter('users', user._id)}
                                        key={user._id}
                                        style={{ cursor: 'pointer', borderRadius: '0' }}
                                        active={filterBy.users.includes(user._id)}
                                        className="border-0 border-start border-primary border-2 position-relative p-3 bg-dark
                                            text-white d-flex justify-content-start gap-3 align-items-center mb-2"
                                    >
                                        <Avatar
                                            imageUrl={user.profilePic?.imageUrl}
                                            name={`${user.firstName} ${user.lastName}`}
                                            size={50}
                                        />
                                        <div className="flex-grow-1">
                                            <span className="text-primary me-3">
                                                {user.firstName} {user.lastName}
                                            </span>
                                            (<code className="text-white">{user.username}</code>)
                                            {user.parentOrganization && (
                                                <p className="small mb-0">
                                                    <span className="fas fa-building me-2" />
                                                    {user.parentOrganization.name}
                                                </p>
                                            )}
                                        </div>
                                        {filterBy.users.includes(user._id) && (
                                            <div className="position-absolute top-0 end-0 mt-2 me-3 small">
                                                Selected <span className="fas fa-check-circle text-success" />
                                            </div>
                                        )}
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => toggleFilterModal()}>
                        <span className="fas fa-check-circle me-2" />
                        Show results
                    </Button>
                </ModalFooter>
            </Modal>

            {isPanelOpen && (
                <ErrorLogDetailPanel
                    logId={logId}
                    onClose={closePanel}
                />
            )}
        </div>
    );
};

export default ErrorLogs;
